import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { ArrowUpRight, ChevronLeft, ChevronRight, Expand } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import Reveal from '@/components/generals/reveal'
import { CtaBand } from '@/components/landing/blocks'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { CURATED_DESIGNS } from '@/constants/designs'
import { encodeConfig } from '@/lib/configurator/validate'

const PROJECTS = Array.from({ length: 30 }, (_, index) => {
    const number = String(index + 1).padStart(2, '0')

    return {
        src: `/images/inspiratie/project-${number}.jpg`,
        alt: `Stalen binnendeur met glas, gerealiseerd project ${index + 1} van ArtDoors`,
        label: `Project ${number}`
    }
})

const InspirationPage: FC = (): ReactNode => {
    const [active, setActive] = useState<number | null>(null)

    const step = (delta: number) => setActive(current => (current === null ? current : (current + delta + PROJECTS.length) % PROJECTS.length))

    const project = active === null ? null : PROJECTS[active]

    return (
        <Layout>
            <Seo title='Inspiratie' description='Laat u inspireren door stalen binnendeuren die ArtDoors bij klanten plaatste. Bekijk onze projecten en ontwerp daarna uw eigen deur in de 3D-configurator.' />

            <section id='introductie' className='mx-auto max-w-[1440px] px-5 pt-16 pb-16 sm:px-8 lg:px-12 lg:pt-24'>
                <div className='grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:items-end'>
                    <div>
                        <p className='ad-rise eyebrow'>Inspiratie</p>
                        <h1 className='ad-rise mt-5 font-display text-5xl leading-[1.02] sm:text-7xl' style={{ animationDelay: '80ms' }}>
                            Deuren die we al plaatsten
                        </h1>
                    </div>

                    <p className='ad-rise text-lg leading-relaxed text-muted-foreground' style={{ animationDelay: '160ms' }}>
                        Een selectie van stalen deuren en wanden die we bij klanten maakten en plaatsten. Klik op een foto om hem groot te bekijken en ontwerp daarna uw eigen deur in de configurator.
                    </p>
                </div>
            </section>

            <section id='uitgelicht' className='mx-auto max-w-[1440px] px-5 pb-20 sm:px-8 lg:px-12'>
                <Reveal className='grid overflow-hidden rounded-md bg-ink text-ink-foreground ring-1 ring-border lg:grid-cols-[1fr_1.1fr]'>
                    <div className='relative min-h-[26rem]'>
                        <Image src='/images/fluted-partition.jpg' alt='Ruimte achter een zwart stalen kader met ribbelglas' fill sizes='(min-width: 1024px) 45vw, 100vw' className='object-cover' priority />
                    </div>

                    <div className='flex flex-col justify-center gap-5 p-8 sm:p-12 lg:p-16'>
                        <p className='eyebrow'>Uitgelicht</p>
                        <h2 className='font-display text-4xl leading-tight sm:text-5xl'>Ribbelglas in zwart staal</h2>
                        <p className='text-base leading-relaxed text-ink-foreground/70'>
                            Ribbelglas is de favoriet van veel interieurontwerpers. De verticale ribbels breken het zicht zacht, terwijl het daglicht vrij spel houdt. In combinatie met slank zwart staal ontstaat een ruimte die open voelt en toch rust geeft. Kies in de configurator voor glas op aanvraag en beschrijf uw wens.
                        </p>
                        <Link href={`/configurator?c=${encodeConfig(CURATED_DESIGNS[1].config)}`} className='mt-2 inline-flex h-12 items-center gap-2 self-start rounded-sm bg-primary px-6 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/85'>
                            Ontwerp met ribbelglas
                            <ArrowUpRight className='size-4' aria-hidden='true' />
                        </Link>
                    </div>
                </Reveal>
            </section>

            <section id='projecten' className='mx-auto max-w-[1440px] px-5 pb-24 sm:px-8 lg:px-12 lg:pb-32'>
                <div className='grid gap-6 sm:grid-cols-2 xl:grid-cols-4'>
                    {PROJECTS.map((item, index) => (
                        <Reveal key={item.src} delay={(index % 4) * 70}>
                            <button
                                type='button'
                                onClick={() => setActive(index)}
                                className='group flex h-full w-full flex-col overflow-hidden rounded-md bg-card text-left ring-1 ring-border transition-shadow outline-none hover:ring-primary/60 focus-visible:ring-3 focus-visible:ring-ring/50'
                                aria-label={`${item.label} groot bekijken`}
                            >
                                <div className='relative aspect-[4/5] overflow-hidden bg-paper'>
                                    <Image
                                        src={item.src}
                                        alt={item.alt}
                                        fill
                                        sizes='(min-width: 1280px) 25vw, (min-width: 640px) 50vw, 100vw'
                                        className='object-cover transition-transform duration-700 group-hover:scale-[1.04]'
                                    />
                                </div>

                                <div className='flex items-center justify-between gap-3 p-5'>
                                    <span className='text-[11px] font-semibold tracking-[0.18em] text-brass-strong uppercase'>{item.label}</span>
                                    <span className='inline-flex items-center gap-1.5 text-xs font-semibold'>
                                        Bekijken
                                        <Expand className='size-4' aria-hidden='true' />
                                    </span>
                                </div>
                            </button>
                        </Reveal>
                    ))}
                </div>
            </section>

            <Dialog open={active !== null} onOpenChange={open => !open && setActive(null)}>
                <DialogContent className='max-w-[min(92vw,1100px)] gap-0 border-border bg-ink p-0 sm:max-w-[min(92vw,1100px)]'>
                    <DialogTitle className='sr-only'>{project?.label ?? 'Project'}</DialogTitle>

                    {project && (
                        <div className='relative h-[80vh] w-full'>
                            <Image src={project.src} alt={project.alt} fill sizes='92vw' className='object-contain' />

                            <button type='button' onClick={() => step(-1)} aria-label='Vorige foto' className='absolute top-1/2 left-3 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-primary hover:text-primary-foreground'>
                                <ChevronLeft className='size-5' aria-hidden='true' />
                            </button>

                            <button type='button' onClick={() => step(1)} aria-label='Volgende foto' className='absolute top-1/2 right-3 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-primary hover:text-primary-foreground'>
                                <ChevronRight className='size-5' aria-hidden='true' />
                            </button>

                            <span className='absolute bottom-3 left-1/2 -translate-x-1/2 rounded-sm bg-black/60 px-3 py-1 text-xs font-semibold text-white tabular-nums'>
                                {(active ?? 0) + 1} / {PROJECTS.length}
                            </span>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <CtaBand title='Uw eigen deur ontwerpen?' text='Laat u inspireren en stel daarna uw eigen deur samen in 3D. U ziet bij elke keuze direct het resultaat en de prijs.' />
        </Layout>
    )
}

export default InspirationPage
